import { useEffect, useMemo } from "react";
import { Box, Text } from "@chakra-ui/react";
import { pick } from "lodash";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { getFragmentData } from "__generated__/gql";
import { makeFormatPercent } from "utils/formatting";
import DistributorPricingRadios from "./DistributorPricingRadios";
import PricingCalculatorSection, {
  PricingCalculatorStat,
} from "./PricingCalculatorSection";
import PricingCurrencyInput from "./PricingCurrencyInput";
import SelectedSkuSection from "./SelectedSkuSection";
import {
  DEFAULT_VALUES,
  INPUT_FIELD_NAMES,
  InputFieldNames,
  UsePricingCalculatorFormReturn,
  skuPricingCalculatorQuery,
  skuPricingCalculationFieldsFragment,
} from "./pricingCalculatorUtils";

type OutputMetrics = Partial<
  Record<
    | "landedCogs"
    | "brandsWholesalePrice"
    | "distributorsWholesalePrice"
    | "srp"
    | "netProfit"
    | "netProfitMargin",
    number
  >
>;

function calculateIfValidDeps(
  calculation: (...args: number[]) => number,
  dependencies: (number | null | undefined)[],
  nonZeroDependencies: (number | null | undefined)[] = [],
): number | undefined {
  if (!dependencies.every((d) => typeof d === "number" && !Number.isNaN(d)))
    return;
  if (!nonZeroDependencies.every((d) => typeof d === "number" && d > 0)) return;
  return calculation(
    ...([...dependencies, ...nonZeroDependencies] as number[]),
  );
}

type Props = {
  formContext: UsePricingCalculatorFormReturn;
  skuId?: UUID;
};

export default function SkuPricingCalculator({ formContext, skuId }: Props) {
  const { control, watch, reset } = formContext;

  const { data, loading } = useBrandQuery(skuPricingCalculatorQuery, {
    skip: !skuId,
    variables: skuId ? { skuId } : undefined,
  });

  useEffect(() => {
    if (data?.sku?.pricingCalculation) {
      reset(
        pick(
          getFragmentData(
            skuPricingCalculationFieldsFragment,
            data.sku.pricingCalculation,
          ),
          INPUT_FIELD_NAMES,
        ),
      );
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [data, reset]);

  const values = watch();

  const { unitCogs: skuUnitCogs } = data?.sku || {};
  const unitCogs = skuId ? skuUnitCogs : values.unitCogs;
  const floatUnitCogs = unitCogs ? parseFloat(unitCogs) : null;

  const outputValues = useMemo<OutputMetrics>(() => {
    const floatValues = (
      Object.keys(values) as unknown as InputFieldNames[]
    ).reduce<
      Partial<
        Omit<Record<InputFieldNames, number | null>, "isDistributorMarkup">
      >
    >((acc, fieldName) => {
      if (fieldName === "isDistributorMarkup") return acc;
      const strValue = values[fieldName];
      acc[fieldName] = strValue ? parseFloat(strValue) : null;
      return acc;
    }, {});

    const { isDistributorMarkup } = values;
    const {
      avgFreightOut,
      grossProfitMargin,
      distributorPercent,
      retailerMargin,
      anticipatedDeduction,
    } = floatValues;

    const landedCogs = calculateIfValidDeps(
      (a, b) => a + b,
      [floatUnitCogs, avgFreightOut],
    );

    const brandsWholesalePrice = calculateIfValidDeps(
      (a, b) => a / b,
      [landedCogs],
      [grossProfitMargin && 1 - grossProfitMargin],
    );

    const distributorsWholesalePrice = calculateIfValidDeps(
      (a, b) => (isDistributorMarkup ? a * (1 + b) : a / (1 - b)),
      [brandsWholesalePrice, distributorPercent],
    );

    const srp = calculateIfValidDeps(
      (a, b) => a / b,
      [distributorsWholesalePrice],
      [retailerMargin && 1 - retailerMargin],
    );

    const netProfit = calculateIfValidDeps(
      (a, b, c) => a - b - a * c,
      [brandsWholesalePrice, landedCogs, anticipatedDeduction],
    );

    const netProfitMargin = calculateIfValidDeps(
      (a, b) => a / b,
      [netProfit],
      [brandsWholesalePrice],
    );

    return {
      landedCogs,
      brandsWholesalePrice,
      distributorsWholesalePrice,
      srp,
      netProfit,
      netProfitMargin,
    };
  }, [values, floatUnitCogs]);

  return (
    <Box pt={6}>
      {skuId ? (
        <SelectedSkuSection skuId={skuId} />
      ) : (
        <PricingCalculatorSection
          formContext={formContext}
          name="unitCogs"
          isLoading={loading}
          inputLabel="Unit COGS"
          InputComponent={PricingCurrencyInput}
        />
      )}

      <PricingCalculatorSection
        formContext={formContext}
        name="avgFreightOut"
        isLoading={loading}
        inputLabel="Average Freight Out Per Order"
        outputLabel="Subtotal Landed COGS"
        outputValue={outputValues.landedCogs}
        InputComponent={PricingCurrencyInput}
      />

      <PricingCalculatorSection
        formContext={formContext}
        name="grossProfitMargin"
        isLoading={loading}
        inputLabel="Gross Profit Margin"
        outputLabel="Brand's Wholesale Price"
        outputValue={outputValues.brandsWholesalePrice}
      />

      <PricingCalculatorSection
        formContext={formContext}
        name="distributorPercent"
        isLoading={loading}
        inputLabel="Distributor"
        outputLabel="Distributor's Wholesale Price"
        outputValue={outputValues.distributorsWholesalePrice}
        afterFormLabel={
          <>
            <DistributorPricingRadios control={control} isLoading={loading} />
            <Text fontSize="xs" color="gray.400">
              optional
            </Text>
          </>
        }
      />

      <PricingCalculatorSection
        formContext={formContext}
        name="retailerMargin"
        isLoading={loading}
        inputLabel="Retailer Margin"
        outputLabel="Suggested Retail Price"
        outputValue={outputValues.srp}
        isOutputHighlighted
      />

      <PricingCalculatorSection
        formContext={formContext}
        name="anticipatedDeduction"
        isLoading={loading}
        inputLabel="Anticipated Deduction"
        outputLabel="Net Profit"
        outputValue={outputValues.netProfit}
        isOutputHighlighted
        outputContentAfter={
          <PricingCalculatorStat
            label="Margin"
            value={makeFormatPercent({ minimumFractionDigits: 1 })(
              outputValues.netProfitMargin,
            )}
            isLoading={loading}
            size="sm"
            fontFamily="body"
            fontStyle="italic"
            labelProps={{ textAlign: "left", fontSize: "md" }}
          />
        }
      />
    </Box>
  );
}
